import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useDispatch } from "react-redux";
import { setShowAddressValidation } from "../../../store/slices/CustomerSlice/CustomerSlice";

export default function ValidateAddressPopup({
  show,
  addressData,
  onAcceptAddress,
  onUseOriginal,
}) {
  const isValid = addressData?.is_valid;
  const hasError = addressData?.error;

  const dispatch = useDispatch();

  const onHide = () => {
    dispatch(setShowAddressValidation(false));
  };

  const renderAddressCard = (
    address,
    title,
    isOriginal = false,
    compareAddress = null
  ) => {
    if (!address) return null; // Helper function to check if a field is different
    const isFieldDifferent = (originalValue, validatedValue) => {
      if (!compareAddress) return false;
      const orig = (originalValue || "").trim();
      const valid = (validatedValue || "").trim();
      return orig !== valid;
    };

    // Helper function to get highlight classes
    const getHighlightClasses = (isDifferent) =>
      isDifferent
        ? "bg-yellow-50 px-1 py-0.5 rounded border border-yellow-200"
        : "";

    // Function to render address with individual part highlighting
    const renderFormattedAddress = () => {
      if (!address) return "N/A";

      const parts = [];

      // Address Line 1
      if (address.address_line_1) {
        const isDifferent =
          compareAddress &&
          isFieldDifferent(
            isOriginal ? address.address_line_1 : compareAddress.address_line_1,
            isOriginal ? compareAddress.address_line_1 : address.address_line_1
          );
        parts.push(
          <span key="line1" className={getHighlightClasses(isDifferent)}>
            {address.address_line_1}
          </span>
        );
      }

      // Address Line 2
      if (address.address_line_2) {
        const isDifferent =
          compareAddress &&
          isFieldDifferent(
            isOriginal ? address.address_line_2 : compareAddress.address_line_2,
            isOriginal ? compareAddress.address_line_2 : address.address_line_2
          );
        parts.push(
          <span key="line2" className={getHighlightClasses(isDifferent)}>
            {address.address_line_2}
          </span>
        );
      }

      // City, State, ZIP combined
      const cityStateZipParts = [];
      if (address.city) {
        const isDifferent =
          compareAddress &&
          isFieldDifferent(
            isOriginal ? address.city : compareAddress.city,
            isOriginal ? compareAddress.city : address.city
          );
        cityStateZipParts.push(
          <span key="city" className={getHighlightClasses(isDifferent)}>
            {address.city}
          </span>
        );
      }

      if (address.state) {
        const isDifferent =
          compareAddress &&
          isFieldDifferent(
            isOriginal ? address.state : compareAddress.state,
            isOriginal ? compareAddress.state : address.state
          );
        cityStateZipParts.push(
          <span key="state" className={getHighlightClasses(isDifferent)}>
            {address.state}
          </span>
        );
      }

      if (address.postal_code || address.zip_code) {
        const zipValue = address.postal_code || address.zip_code;
        const isDifferent =
          compareAddress &&
          isFieldDifferent(
            isOriginal
              ? zipValue
              : compareAddress.postal_code || compareAddress.zip_code,
            isOriginal
              ? compareAddress.postal_code || compareAddress.zip_code
              : zipValue
          );
        cityStateZipParts.push(
          <span key="zip" className={getHighlightClasses(isDifferent)}>
            {zipValue}
          </span>
        );
      }

      if (cityStateZipParts.length > 0) {
        const cityStateZipElement = cityStateZipParts.reduce(
          (prev, curr, index) =>
            prev === null ? [curr] : [...prev, ", ", curr],
          null
        );
        parts.push(<span key="cityStateZip">{cityStateZipElement}</span>);
      }

      // Country
      if (
        address.country &&
        address.country !== "US" &&
        address.country !== "USA"
      ) {
        const isDifferent =
          compareAddress &&
          isFieldDifferent(
            isOriginal ? address.country : compareAddress.country,
            isOriginal ? compareAddress.country : address.country
          );
        parts.push(
          <span key="country" className={getHighlightClasses(isDifferent)}>
            {address.country}
          </span>
        );
      }

      // Join parts with commas
      return parts.reduce(
        (prev, curr, index) => (prev === null ? [curr] : [...prev, ", ", curr]),
        null
      );
    };

    return (
      <div
        className={`mb-6 rounded-lg border-2 ${
          isOriginal ? "border-yellow-400" : "border-green-400"
        } bg-white shadow-sm`}
      >
        <div className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-t-lg">
          <h6 className="text-lg font-semibold text-gray-900">{title}</h6>
          {!isOriginal && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircleIcon className="w-4 h-4 mr-1" />
              Validated
            </span>
          )}
          {isOriginal && !isValid && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
              Original
            </span>
          )}
        </div>{" "}
        <div className="p-4">
          <div className="grid grid-cols-1 gap-4">
            {" "}
            <div>
              <span className="font-semibold text-gray-700">Address: </span>
              <span>{renderFormattedAddress()}</span>
            </div>
            {address.address_classification && (
              <div>
                <span className="font-semibold text-gray-700">
                  Classification:{" "}
                </span>
                {address.address_classification.Description}
                {address.address_classification.Code === "2" && (
                  <div className="mt-2 rounded-md bg-yellow-50 p-3 border border-yellow-200">
                    <div className="flex">
                      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mr-2" />
                      <div>
                        <h4 className="text-sm font-medium text-yellow-800">
                          Residential Delivery Fee
                        </h4>
                        <p className="text-sm text-yellow-700">
                          A residential delivery fee will be added for delivery
                          to this address.
                        </p>
                      </div>
                    </div>
                  </div>
                )}{" "}
              </div>
            )}
            {address.region && (
              <div>
                <span className="font-semibold text-gray-700">Region: </span>
                {address.region}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onHide}>
        <Transition.Child
          as="div"
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="fixed inset-0 bg-black bg-opacity-25"
        />{" "}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
              className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all"
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <Dialog.Title className="flex items-center text-lg font-medium leading-6 text-gray-900">
                  {isValid ? (
                    <>
                      <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" />
                      <span>Address Validation</span>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="w-6 h-6 text-red-500 mr-2" />
                      <span>Address Validation</span>
                    </>
                  )}
                </Dialog.Title>
                <button
                  onClick={onHide}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-4">
                {!addressData && (
                  <div className="mb-4 rounded-md bg-yellow-50 p-4 border border-yellow-200">
                    <div className="flex">
                      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mr-2" />
                      <div>
                        <h3 className="text-sm font-medium text-yellow-800">
                          No address data provided
                        </h3>
                        <p className="text-sm text-yellow-700">
                          Unable to display validation results.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {hasError && (
                  <div className="mb-4 rounded-md bg-red-50 p-4 border border-red-200">
                    <div className="flex">
                      <XCircleIcon className="w-5 h-5 text-red-400 mr-2" />
                      <div>
                        <h3 className="text-sm font-medium text-red-800">
                          Address Error
                        </h3>
                        <p className="text-sm text-red-700">
                          This address may not be valid, please review before
                          proceeding. Please note, we automatically apply
                          residential fees to unknown addresses. If you believe
                          this is an error, contact your representative for
                          review after submitting your order.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {isValid && (
                  <div className="mb-4 rounded-md bg-green-50 p-4 border border-green-200">
                    <div className="flex">
                      <CheckCircleIcon className="w-5 h-5 text-green-400 mr-2" />
                      <div>
                        <h3 className="text-sm font-medium text-green-800">
                          Address found!
                        </h3>
                        <p className="text-sm text-green-700">
                          The recommended address is pulled from national
                          databases, so it is standardized.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {addressData && (
                  <div>
                    {/* Original Address */}
                    {renderAddressCard(
                      addressData?.original_address,
                      "Original Address",
                      true,
                      isValid
                        ? {
                            address_line_1: addressData.address_line_1,
                            address_line_2: addressData.address_line_2,
                            city: addressData.city,
                            state: addressData.state,
                            postal_code: addressData.postal_code,
                            country: addressData.country,
                          }
                        : null
                    )}

                    {/* Validated Address (if valid) */}
                    {isValid &&
                      renderAddressCard(
                        {
                          address_line_1: addressData.address_line_1,
                          address_line_2: addressData.address_line_2,
                          city: addressData.city,
                          state: addressData.state,
                          postal_code: addressData.postal_code,
                          postal_code_extended:
                            addressData.postal_code_extended,
                          country: addressData.country,
                          region: addressData.region,
                          address_classification:
                            addressData.address_classification,
                        },
                        "Recommended Address",
                        false,
                        addressData?.original_address
                      )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={onHide}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Cancel
                </button>

                {isValid ? (
                  <>
                    <button
                      onClick={() =>
                        onUseOriginal(addressData.original_address)
                      }
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Use Original
                    </button>
                    <button
                      onClick={() => onAcceptAddress(addressData)}
                      className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Use Validated Address
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => onUseOriginal(addressData.original_address)}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Use Original Address
                  </button>
                )}
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
